import { UntitledIcon, type UntitledIconData } from '@faceup/icons'
import { ulCheck } from '@faceup/icons/ulCheck'
import { usMicrophone01 } from '@faceup/icons/usMicrophone01'
import { usPlay } from '@faceup/icons/usPlay'
import { usStop } from '@faceup/icons/usStop'
import { usTrash04 } from '@faceup/icons/usTrash04'
import { defineMessages, FormattedMessage, useIntl } from '@faceup/localization'
import { Button, Space } from '@faceup/ui-base'
import { createRecorderProcessor, LameEncoder, VOICE_MESSAGE_MAX_DURATION_MS } from '@faceup/utils'
import { useTimeout } from '@mantine/hooks'
import { type RefObject, useEffect, useRef } from 'react'
import { useMedia } from '../../mq'
import { RecordingStatus, type RecordType } from './types'
import { VoiceRecordingWaveCanvas } from './VoiceRecordingWaveCanvas'
import { voiceDistortion } from './voiceDistortion'

const messages = defineMessages('followUp.components.voiceRecording.voiceRecordingBody', [
  { id: 'title', defaultMessage: '' },
  { id: 'description', defaultMessage: '' },
  { id: 'buttonInitializing', defaultMessage: '' },
  { id: 'buttonRecord', defaultMessage: '' },
  { id: 'buttonStopRecording', defaultMessage: '' },
  { id: 'buttonPlay', defaultMessage: '' },
  { id: 'buttonStopPlaying', defaultMessage: '' },
  { id: 'buttonSend', defaultMessage: '' },
  { id: 'buttonDelete', defaultMessage: '' },
] as const)

type Props = {
  recording: RecordType | null
  setRecording: (recording: RecordType | null) => void
  mediaStream: MediaStream | null
  setMediaStream: (stream: MediaStream | null) => void
  audioRef: RefObject<HTMLAudioElement | null>
  recordingStatus: RecordingStatus
  onRecordingStatusChange: (status: RecordingStatus) => void
  onRecordingSave: (recording: RecordType | null) => void
}

export const VoiceRecordingBody = ({
  recording,
  setRecording,
  mediaStream,
  setMediaStream,
  audioRef,
  recordingStatus,
  onRecordingStatusChange,
  onRecordingSave,
}: Props) => {
  const isMdDown = useMedia('mdDown')
  const { formatMessage } = useIntl()
  const { start: startTimeout, clear: clearTimeout } = useTimeout(
    () => stopRecording(),
    VOICE_MESSAGE_MAX_DURATION_MS
  )

  const recordingStartTime = useRef<number>(0)
  const recordingContext = useRef<AudioContext | null>(null)
  const microphone = useRef<MediaStreamAudioSourceNode | null>(null)
  const processor = useRef<AudioWorkletNode | null>(null)
  const lameEncoder = useRef<LameEncoder>(new LameEncoder({ bitRate: 128, sampleRate: 44100 }))

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only run on mount to initialize encoder once
  useEffect(() => {
    const initLameEncoder = async () => {
      if (!lameEncoder.current.isInitialized) {
        onRecordingStatusChange(RecordingStatus.Initializing)
        await lameEncoder.current.initialize()
      }
      onRecordingStatusChange(RecordingStatus.Idle)
    }

    initLameEncoder()
  }, [])

  const addMicrophoneListener = async (context: AudioContext, stream: MediaStream) => {
    // Prevent the weird noise once you start listening to the microphone https://github.com/closeio/mic-recorder-to-mp3/blob/master/src/mic-recorder.js#L34-L37
    let timerToStart: ReturnType<typeof setTimeout> | null = null
    timerToStart = setTimeout(() => (timerToStart = null), 300)

    microphone.current = context.createMediaStreamSource(stream)

    processor.current = await createRecorderProcessor(context)
    processor.current.port.onmessage = event => {
      if (event.data.action === 'encode') {
        if (timerToStart) {
          return
        }

        lameEncoder.current.encode(event.data.buffer)
      }
    }

    microphone.current.connect(processor.current)
    processor.current.connect(context.destination)
  }

  const getFinalVoiceRecordingBlob = async () => {
    const finalBuffer = lameEncoder.current.finish()

    if (finalBuffer.length === 0) {
      throw new Error('No buffer to send')
    }

    // @ts-expect-error Ignore ArrayBuffer type
    const audioBlob = new Blob(finalBuffer, { type: 'audio/mp3' })
    lameEncoder.current.clearBuffer()

    return audioBlob
  }

  const source = recording?.data ? URL.createObjectURL(recording.data) : ''

  const playRecord = () => {
    onRecordingStatusChange(RecordingStatus.Playing)
    audioRef.current?.play()
  }

  const pauseRecord = () => {
    onRecordingStatusChange(RecordingStatus.Paused)
    audioRef.current?.pause()
  }

  const startRecording = async () => {
    if (recordingStatus === RecordingStatus.Recording) {
      return
    }

    // code for audio recording from here https://github.com/closeio/mic-recorder-to-mp3/pull/44
    recordingContext.current = new AudioContext()
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    setMediaStream(stream)
    const distortedStream = voiceDistortion(stream)
    // if you want to disable distorting voice, pass `stream` instead of `distortedStream`
    await addMicrophoneListener(recordingContext.current, distortedStream)

    recordingStartTime.current = Date.now()
    startTimeout()
    onRecordingStatusChange(RecordingStatus.Recording)
  }

  const stopRecording = async () => {
    if (recordingStatus !== RecordingStatus.Recording) {
      return
    }

    if (processor.current && microphone.current) {
      // Clean up the Web Audio API resources.
      processor.current.port.postMessage({ action: 'stop' })
      microphone.current.disconnect()
      processor.current.disconnect()

      // If all references using context are destroyed, context is closed
      // automatically. DOMException is fired when trying to close again
      if (recordingContext.current && recordingContext.current.state !== 'closed') {
        recordingContext.current.close()
      }
    }

    const startTime = recordingStartTime.current
    recordingStartTime.current = 0
    onRecordingStatusChange(startTime ? RecordingStatus.Idle : RecordingStatus.Paused)

    if (startTime) {
      const duration = Date.now() - startTime
      const data = await getFinalVoiceRecordingBlob()

      setRecording({
        data,
        duration,
      })
    }

    onRecordingStatusChange(RecordingStatus.Paused)

    if (mediaStream) {
      clearTimeout()
      mediaStream.getAudioTracks().forEach(track => track.stop())
    }
  }

  const isRecorded = Boolean(recording)

  const getRecordButtonProps = () => {
    if (recordingStatus === RecordingStatus.Initializing) {
      return { onClick: undefined, label: messages.buttonInitializing }
    }
    if (recordingStatus === RecordingStatus.Recording) {
      return { onClick: stopRecording, label: messages.buttonStopRecording }
    }
    return { onClick: startRecording, label: messages.buttonRecord }
  }

  const recordButtonProps = getRecordButtonProps()

  return (
    <div className='w-full min-h-full flex flex-col items-center text-center flex-nowrap'>
      <div style={{ padding: isMdDown ? '7rem 2rem 0' : 0 }}>
        <FormattedMessage message={messages.description} />
      </div>

      <VoiceRecordingWaveCanvas
        width={isMdDown ? 400 : 600}
        height={isMdDown ? 200 : 300}
        status={recordingStatus}
        startTime={recordingStartTime.current ?? 0}
        stream={mediaStream ?? null}
        style={{ marginTop: '1rem', marginBottom: '1rem' }}
      />

      {isRecorded ? (
        <>
          <audio ref={audioRef} onEnded={() => onRecordingStatusChange(RecordingStatus.Paused)}>
            <source src={source} type='audio/mpeg' />
          </audio>
          <Space size={40} align='center'>
            <Button
              type='link'
              danger
              shape='circle'
              onClick={() => {
                setRecording(null)
                onRecordingStatusChange(RecordingStatus.Idle)
              }}
              icon={<Icon icon={usTrash04} />}
              label={formatMessage(messages.buttonDelete)}
            />
            <Button
              onClick={recordingStatus === RecordingStatus.Playing ? pauseRecord : playRecord}
              icon={<Icon icon={recordingStatus === RecordingStatus.Playing ? usStop : usPlay} />}
              label={formatMessage(
                recordingStatus === RecordingStatus.Playing
                  ? messages.buttonStopPlaying
                  : messages.buttonPlay
              )}
              shape='circle'
            />
            <Button
              type='link'
              onClick={() => {
                pauseRecord()
                onRecordingSave(recording)
              }}
              icon={<Icon icon={ulCheck} />}
              label={formatMessage(messages.buttonSend)}
              shape='circle'
            />
          </Space>
        </>
      ) : (
        <Button
          loading={recordingStatus === RecordingStatus.Initializing}
          type='primary'
          shape='circle'
          onClick={recordButtonProps.onClick}
          icon={<Icon icon={usMicrophone01} />}
          label={formatMessage(recordButtonProps.label)}
        />
      )}
    </div>
  )
}

const Icon = ({ icon }: { icon: UntitledIconData }) => {
  return <UntitledIcon icon={icon} size={24} />
}
